/*
 * The ResUI Select's option rules (PRD-1702), from the Navy handoff's
 * selects.js and ResUI Design Decisions, Select.
 */

const LEADING_PLACEHOLDER = /^\W*(select|choose)\b/i;

/**
 * Options as the menu lists them: `[{ value, label }]` in, the same out plus the
 * field's placeholder.
 * - A first option with no value that reads "Select…" or "Choose…" becomes the
 *   placeholder, shown in text-grey while nothing is chosen ("- Select -" reads
 *   "Select").
 * - Other options with no value ("- none available -") lead.
 * - The rest are alphabetical, numbers in number order, unless `scale`: an
 *   ordered scale (sort orders, date ranges) keeps its own order.
 */
export function menuOptions(options, { scale = false } = {}) {
  let list = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  let placeholder = '';
  if (list[0] && list[0].value === '' && LEADING_PLACEHOLDER.test(list[0].label)) {
    placeholder = list[0].label.replace(/^[\s-]+|[\s-]+$/g, '');
    list = list.slice(1);
  }
  let lead = 0;
  while (list[lead] && list[lead].value === '') lead += 1;
  if (!scale) {
    const rest = list.slice(lead).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
    list = [...list.slice(0, lead), ...rest];
  }
  return { list, placeholder };
}

/**
 * Type to jump. Letters within half a second build one search; one letter
 * repeated steps through the options that start with it. Returns the index, or
 * -1. `state` carries the typing between calls. `from` is the current option,
 * or -1 for none, so a single letter then starts at the first.
 */
export function typeAhead(state, key, from, labels) {
  const now = Date.now();
  state.typed = now - (state.at || 0) > 500 ? key : (state.typed || '') + key;
  state.at = now;
  const q = state.typed.toLowerCase();
  const same = q.split('').every((c) => c === q[0]);
  const start = same ? from + 1 : Math.max(0, from);
  for (let n = 0; n < labels.length; n += 1) {
    const k = (start + n) % labels.length;
    if (labels[k].toLowerCase().indexOf(same ? q[0] : q) === 0) return k;
  }
  return -1;
}

/**
 * Where the menu goes: gap-2 below the field and left-aligned; right-aligned
 * when that would leave the window, above when only that fits, else the side
 * with more room; always 16 inside. As wide as the field, 180 at least.
 */
export function placeMenu(field, menuWidth, menuHeight) {
  const r = field.getBoundingClientRect();
  const width = Math.max(180, r.width, menuWidth);
  let left = r.left;
  if (left + width > window.innerWidth - 16) left = r.right - width;
  left = Math.max(16, Math.min(left, window.innerWidth - 16 - width));
  const below = window.innerHeight - 16 - (r.bottom + 8);
  const above = r.top - 8 - 16;
  let top = menuHeight <= below || below >= above ? r.bottom + 8 : r.top - 8 - menuHeight;
  top = Math.max(16, Math.min(top, window.innerHeight - 16 - menuHeight));
  return { left, top, minWidth: Math.max(180, r.width) };
}
